import { EditorApp, EditorMode, IntentType } from './constants';
import { getToken } from './storage';

const TOKEN_SIGNATURE_PLACEHOLDER = '.SIGNATURE_REMOVED';

type Group = {
  id: string;
  index?: number;
  required?: boolean;
};

type Document = {
  id: string;
  mode: EditorMode;
  elp?: number; // This represents 'edit level permissions' that a user would have on the editor for this document.
  groups?: Array<Group>;
  rev?: number;
};
export interface ZDocsClaims {
  intent?: IntentType;
  app?: EditorApp; // This represents the 'app experience' of our editor, if undefined, assume `DEFAULT` mode
  name: string;
  tenantId: string;
  schema: string;
  platformAdminLevel?: string;
  dact?: {
    id: string;
    type: 'SIGN';
  };
  documents?: Array<Document>;
  signer?: {
    authMethod: string;
    emailAddress?: string;
    initials: string;
    name: string;
  };
}

export interface TokenPayload {
  iss: string;
  sub: string;
  aud: string | Array<string>;
  exp: number;
  iat: number;
  jti: string;
  zdocs: ZDocsClaims;
}

type Group_V2 = {
  id: string;
  idx?: number;
  rqd: boolean;
};

type Document_V2 = {
  ids: Array<string>;
  mode: EditorMode;
  elp?: number;
  grps?: Array<Group_V2>;
  rev?: number;
};

interface ZDocsClaims_V2 {
  tid: string;
  intent?: IntentType;
  app?: EditorApp;
  name: string;
  schema: string;
  pal?: string;
  dact?: {
    id: string;
    type: 'SIGN';
  };
  docs?: Array<Document_V2>;
  sgnr?: {
    auth: string;
    email?: string;
    intl: string;
    name: string;
  };
}

interface TokenPayload_V2 {
  iss: string;
  sub: string;
  aud: string | Array<string>;
  exp: number;
  iat: number;
  jti: string;
  zdocs: ZDocsClaims_V2;
}

export interface TokenInfo {
  token: string;
  redactedToken: string;
  payload: TokenPayload;
  redactedPayload: TokenPayload;
}

function decodeJwt(jwt: string): string {
  const base64Url = jwt.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
}

export function getJwt(): string {
  const jwt = getToken();

  return jwt || '';
}

function convertV2TokenToV1(tokenJsonV2: TokenPayload_V2) {
  const claimsV2 = tokenJsonV2.zdocs;
  const documents = claimsV2.docs?.reduce((acc: Array<Document>, doc) => {
    const docMap = doc.ids.map(id => ({
      mode: doc.mode,
      groups: doc.grps?.reduce((acc: Array<Group>, group) => {
        acc.push({
          id: group.id,
          index: group.idx,
          required: group.rqd,
        });
        return acc;
      }, []),
      id,
      elp: doc.elp,
      rev: doc.rev,
    }));
    acc.push(...docMap);
    return acc;
  }, []);
  const tokenJsonV1 = {
    ...tokenJsonV2,
    zdocs: {
      intent: claimsV2.intent,
      app: claimsV2.app,
      name: claimsV2.name,
      tenantId: claimsV2.tid,
      schema: claimsV2.schema,
      platformAdminLevel: claimsV2.pal,
      dact: claimsV2.dact,
      documents,
      signer: claimsV2.sgnr
        ? {
            authMethod: claimsV2.sgnr.auth,
            emailAddress: claimsV2.sgnr.email,
            initials: claimsV2.sgnr.intl,
            name: claimsV2.sgnr.name,
          }
        : undefined,
    },
  };
  return tokenJsonV1;
}

function getEditorApp(zdocsClaims: ZDocsClaims) {
  switch (zdocsClaims.intent) {
    case IntentType.READ:
    case IntentType.FILL:
    case IntentType.SIGN:
      return EditorApp.Default;
    case IntentType.VIEW:
    case IntentType.EDIT:
      return EditorApp.TemplateEditor;
    case IntentType.SELF_SERVE:
      return EditorApp.SelfServe;
    case IntentType.ANNOTATE:
      return EditorApp.Annotation;
    case IntentType.COMPOSE:
      return EditorApp.OrganizePages;
  }

  return zdocsClaims.app ?? EditorApp.Default;
}

function getTokenPayload(tokenJson: any) {
  let tokenPayload: TokenPayload | null = null;

  if (tokenJson.zdocs && tokenJson.zdocs.schema === '2') {
    tokenPayload = convertV2TokenToV1(tokenJson as TokenPayload_V2);
  } else {
    tokenPayload = tokenJson as TokenPayload;
  }

  if (tokenPayload.zdocs && !tokenPayload.zdocs.app) {
    tokenPayload.zdocs.app = getEditorApp(tokenPayload.zdocs);
  }

  return tokenPayload;
}

export function getPayloadFromToken(token: string) {
  try {
    const tokenJsonString = decodeJwt(token);
    const tokenJson = JSON.parse(tokenJsonString);
    return getTokenPayload(tokenJson);
  } catch (e) {}

  return null;
}

function redactToken(value: string, tokenInfo?: TokenInfo) {
  if (tokenInfo) {
    value = value.replace(
      new RegExp(tokenInfo.token, 'g'),
      tokenInfo.redactedToken
    );
  }

  return value
    .replace(/token=[^&#"]*/g, (match: string) => {
      if (match.endsWith(TOKEN_SIGNATURE_PLACEHOLDER)) {
        return match;
      }

      return 'token=REMOVED';
    })
    .replace(/source=[^&"]*/g, 'source=REMOVED');
}

export function redactTokenSignature(token: string) {
  const signatureIndex = token.lastIndexOf('.');

  return `${token.substring(0, signatureIndex)}${TOKEN_SIGNATURE_PLACEHOLDER}`;
}

export function redactTokenFromPayload(
  payload: { [key: string]: any },
  tokenInfo?: TokenInfo
) {
  for (const propName in payload) {
    const propValue = payload[propName];

    if (propValue) {
      switch (typeof propValue) {
        case 'string':
          const propDesc = Object.getOwnPropertyDescriptor(payload, propName);

          if (propDesc?.writable) {
            payload[propName] = redactToken(propValue as string, tokenInfo);
          }
          break;
        case 'object':
          redactTokenFromPayload(propValue, tokenInfo);
          break;
      }
    }
  }

  return payload;
}

export function redactTokenPayload(tokenPayload: TokenPayload) {
  // Remove potential PII
  const zDocsClaims: Partial<ZDocsClaims> = { ...tokenPayload.zdocs };
  delete zDocsClaims.name;
  delete zDocsClaims.signer;

  return { ...tokenPayload, zdocs: zDocsClaims as ZDocsClaims };
}

export function isValidToken(token: string): boolean {
  if (!!token) {
    const payload = getPayloadFromToken(token);

    if (!!payload) {
      const { exp } = payload;

      // For the JWT, this is _seconds_ since epoch,
      // so we need to translate to milliseconds since epoch
      const expirationTime = exp * 1000;
      const currentTime = new Date().getTime();

      return expirationTime > currentTime;
    }
  }

  return false;
}

export function getJwtPayload(): TokenInfo | null {
  const token = getToken();
  const payload = token ? getPayloadFromToken(token) : undefined;

  if (token && payload) {
    const redactedToken = redactTokenSignature(token);
    const redactedPayload = redactTokenPayload(payload);
    return { token, redactedToken, payload, redactedPayload };
  }

  return null;
}

export function getLoggedInName() {
  const { payload } = getJwtPayload() || {};

  if (!!payload) {
    return !!payload.zdocs && !!payload.zdocs.name ? payload.zdocs.name : '';
  }

  return '';
}

export function getAuthorizedAdminId() {
  const { payload } = getJwtPayload() || {};

  if (payload && payload.sub && payload.sub.startsWith('admin/')) {
    return payload.sub.replace(/^admin\//, '');
  }
  return null;
}
